import { api } from "../api";
import { getAnalyticsDateRange } from "./getAnalyticsDateRange";

const retryWithBackoff = async (fn, valueExtractor) => {
  const maxRetries = 3;
  const delay = 300;
  for (let i = 0; i < maxRetries; i++) {
    const result = await fn();
    const value = valueExtractor(result);
    if (value !== null && value !== undefined) {
      return result;
    }
    if (i < maxRetries - 1) {
      await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
    }
  }
  return await fn();
};

export const fetchCampaignAnalyticsStats = async ({
  customerId,
  shopId,
  campaignId,
  selectedDateFilter,
  startDate,
  endDate,
  onUpdate,
  scenarioType,
}) => {
  const { from, to } = getAnalyticsDateRange(
    selectedDateFilter,
    startDate,
    endDate,
  );

  const commonParams = { customerId, campaignId, from, to };
  const allTimeParams = {
    customerId,
    campaignId,
    from: new Date(0),
    to: new Date(),
  };
  const shopParams = { shopId, campaignId, from, to };
  const suffix = selectedDateFilter;

  const metricsFetchers = [
    {
      fetch: () =>
        retryWithBackoff(
          () =>
            scenarioType == "campaign"
              ? api.getAnalyticsSentByCampaignId(commonParams)
              : api.getAnalyticsSentByAutomationId(commonParams),
          (r) => r?.messages?.sent,
        ),
      transform: (result) => ({
        [`sent${suffix}`]: result?.messages?.sent ?? 0,
      }),
    },
    ...(scenarioType === "campaign" && selectedDateFilter !== "AllTime"
      ? [
          {
            fetch: () =>
              retryWithBackoff(
                () => api.getAnalyticsSentByCampaignId(allTimeParams),
                (r) => r?.messages?.sent,
              ),
            transform: (result) => ({
              [`sentAllTime`]: result?.messages?.sent ?? 0,
            }),
          },
        ]
      : []),
    ...(scenarioType === "campaign" && selectedDateFilter !== "AllTime"
      ? [
          {
            fetch: () =>
              retryWithBackoff(
                () => api.getAnalyticsMessagesCountByCampaignId(allTimeParams),
                (r) => r?.messages?.messagesCount,
              ),
            transform: (result) => ({
              [`messagesCountAllTime`]: result?.messages?.messagesCount ?? 0,
            }),
          },
        ]
      : []),
    {
      fetch: () =>
        retryWithBackoff(
          () =>
            scenarioType == "campaign"
              ? api.getAnalyticsDeliveredByCampaignId(commonParams)
              : api.getAnalyticsDeliveredByAutomationId(commonParams),
          (r) => r?.messages?.delivered,
        ),
      transform: (result) => ({
        [`delivered${suffix}`]: result?.messages?.delivered ?? 0,
      }),
    },
    ...(scenarioType === "campaign" && selectedDateFilter !== "AllTime"
      ? [
          {
            fetch: () =>
              retryWithBackoff(
                () => api.getAnalyticsDeliveredByCampaignId(allTimeParams),
                (r) => r?.messages?.delivered,
              ),
            transform: (result) => ({
              [`deliveredAllTime`]: result?.messages?.delivered ?? 0,
            }),
          },
        ]
      : []),
    {
      fetch: () =>
        retryWithBackoff(
          () =>
            scenarioType == "campaign"
              ? api.getAnalyticsReadByCampaignId(commonParams)
              : api.getAnalyticsReadByAutomationId(commonParams),
          (r) => r?.messages?.read,
        ),
      transform: (result) => ({
        [`read${suffix}`]: result?.messages?.read ?? 0,
      }),
    },
    ...(scenarioType === "campaign" && selectedDateFilter !== "AllTime"
      ? [
          {
            fetch: () =>
              retryWithBackoff(
                () => api.getAnalyticsReadByCampaignId(allTimeParams),
                (r) => r?.messages?.read,
              ),
            transform: (result) => ({
              [`readAllTime`]: result?.messages?.read ?? 0,
            }),
          },
        ]
      : []),
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsMessagesCountByCampaignId(commonParams),
          (r) => r?.messages?.messagesCount,
        ),
      transform: (result) => ({
        [`messagesCount${suffix}`]: result?.messages?.messagesCount ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsWhatsappCostByCampaignId(commonParams),
          (r) => r?.messages?.totalWhatsappCostInEuros,
        ),
      transform: (result) => ({
        [`totalWhatsappCostInEuros${suffix}`]:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
        totalWhatsappCostInEuros:
          result?.messages?.totalWhatsappCostInEuros ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsExternalTurnoverByCampaignId(commonParams),
          (r) => r?.messages?.externalTurnover,
        ),
      transform: (result) => ({
        [`externalTurnover${suffix}`]: result?.messages?.externalTurnover ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsOptoutsByCampaignId(commonParams),
          (r) => r?.contacts?.optoutCount,
        ),
      transform: (result) => ({
        [`optoutCount${suffix}`]: result?.contacts?.optoutCount ?? 0,
      }),
    },
    {
      fetch: () =>
        retryWithBackoff(
          () => api.getAnalyticsFailedByCampaignId(commonParams),
          (r) => r?.messages,
        ),
      transform: (result) => ({
        [`failedMessages${suffix}`]: result?.messages ?? [],
      }),
    },
  ];

  if (shopId) {
    metricsFetchers.push(
      {
        fetch: () =>
          retryWithBackoff(
            () => api.getAnalyticsTurnoverByCampaignId(shopParams),
            (r) => r?.shopifyOrders?.turnover,
          ),
        transform: (result) => ({
          [`turnover${suffix}`]: result?.shopifyOrders?.turnover ?? 0,
        }),
      },
      {
        fetch: () =>
          retryWithBackoff(
            () => api.getAnalyticsSalesByCampaignId(shopParams),
            (r) => r?.shopifyOrders?.sales,
          ),
        transform: (result) => ({
          [`sales${suffix}`]: result?.shopifyOrders?.sales ?? 0,
        }),
      },
    );
  }

  const promises = metricsFetchers.map(async ({ fetch, transform }) => {
    try {
      const result = await fetch();
      const partialStats = transform(result);
      onUpdate((prev) => ({ ...prev, ...partialStats }));
    } catch (error) {
      console.error("Error fetching campaign metric:", error);
    }
  });

  await Promise.all(promises);
};
